<template>
  <component
    :is="tag"
    :to="tag === 'router-link' ? to : undefined"
    :type="tag === 'button' ? type : undefined"
    :class="['se-btn', `se-${styleSet}`, variantClass, { disabled: disabled || loading }, $attrs.class]"
    :disabled="tag === 'button' ? (disabled || loading) : undefined"
    :aria-disabled="disabled || loading ? 'true' : undefined"
    :aria-busy="loading"
    @click="handleClick"
  >
    <span v-if="loading" class="se-loading-spinner" aria-hidden="true">
      <svg class="se-spinner-svg" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-dasharray="31.4 31.4" />
      </svg>
    </span>
    <span v-if="loading && loadingText" class="se-loading-text">
      {{ loadingText }}
    </span>
    <slot v-else />
  </component>
</template>

<script setup>
import { computed } from 'vue'

const emit = defineEmits(['click'])

const props = defineProps({
  variant: { type: String, default: 'primary' }, // primary | secondary | text
  styleSet: {
    type: String,
    default: 'set5',
    validator: (value) => ['set5', 'set8'].includes(value),
  }, // set5: standard; set8: compact toolbar
  disabled: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  loadingText: { type: String, default: '' },
  type: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'submit', 'reset'].includes(value),
  },
  to: { type: [String, Object], default: null }, // Add 'to' prop for router-link
})

// Disabled/loading route actions render as native disabled buttons so keyboard and
// pointer activation cannot navigate through RouterLink.
const tag = computed(() => (props.to && !props.disabled && !props.loading ? 'router-link' : 'button'))

const variantClass = computed(() => `se-${props.variant}`)

const handleClick = (event) => {
  if (props.disabled || props.loading) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  emit('click', event)
}
</script>

<style scoped>
/* Shared base */
.se-btn {
  font-family: var(--font-family-body);
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  text-decoration: none;
  transition: var(--btn-transition);
}

.se-btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.se-btn:disabled,
.se-btn.disabled {
  opacity: var(--btn-disabled-opacity);
  cursor: var(--btn-disabled-cursor);
  transform: none;
  pointer-events: none;
}

/* Ensure utility width classes like 'w-full' reliably expand the button */
.se-btn.w-full { display: flex; width: 100%; }

/* Shared icon-only button variants.
   Use --text for row/header-aligned delete actions and --field for actions aligned to field boxes. */
.se-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  background: none;
  border: 1px solid var(--border-subtle);
  color: var(--text-muted);
  transition: var(--btn-transition);
}

.se-icon-button :deep(svg) {
  width: var(--btn-font-size-default);
  height: var(--btn-font-size-default);
}

.se-icon-button--text {
  width: 1.875rem;
  height: 1.875rem;
  border-radius: var(--radius-sm);
}

.se-icon-button--field {
  width: var(--btn-height-secondary);
  height: var(--btn-height-secondary);
  border-radius: var(--radius-md);
  align-self: flex-start;
  margin-top: 0;
}

.se-icon-button--field :deep(svg) {
  width: var(--btn-font-size-default);
  height: var(--btn-font-size-default);
}

/* Variants generic sizing */
.se-primary { min-height: var(--btn-height-primary); padding: var(--btn-padding-primary); border-radius: var(--btn-radius-default); font-weight: var(--btn-font-weight); }
.se-secondary { min-height: var(--btn-height-secondary); padding: var(--btn-padding-secondary); border-radius: var(--btn-radius-default); font-weight: var(--btn-font-weight); }
.se-text { min-height: var(--btn-height-secondary); padding: var(--space-1) var(--space-2); background: transparent; border-radius: var(--radius-sm); font-weight: var(--btn-font-weight); }

/* Style Set 5: Sharp Professional */
.se-set5.se-primary { background: var(--accent); color: var(--btn-primary-contrast); font-weight: 700; letter-spacing: var(--btn-letter-spacing); font-size: var(--font-size-body); }
.se-set5.se-primary:hover { transform: var(--btn-hover-lift); box-shadow: var(--btn-shadow-hover); }
.se-set5.se-secondary { background: transparent; color: var(--accent); border:1px solid var(--border-subtle); }
.se-set5.se-text { color: var(--accent); text-transform: uppercase; }

/* Style Set 8: Compact Toolbar (dense, square) */
.se-set8.se-primary { min-height: var(--btn-height-small); padding: var(--btn-padding-small); border-radius: var(--btn-radius-default); background: var(--accent); color: var(--btn-primary-contrast); font-weight: 600; }
.se-set8.se-primary:hover { background: var(--accent-hover); }
.se-set8.se-secondary { min-height: var(--btn-height-small); padding: var(--btn-padding-secondary); border-radius: var(--btn-radius-default); background: transparent; color: var(--accent); border:1px solid var(--accent); }
.se-set8.se-secondary:hover { background: color-mix(in srgb, var(--accent) 8%, transparent); }
.se-set8.se-text { min-height: var(--btn-height-small); padding: var(--space-1) var(--space-2); color: var(--accent); border-radius: var(--btn-radius-default); }
/* Keep the owning component's foreground color over translucent hover surfaces. */
.se-set8.se-text:hover { background: color-mix(in srgb, var(--accent) 10%, transparent); }

/* Fallback for browsers that do not support color-mix().
   Apply a simple brightness filter so the hover is visibly darker even without color-mix. */
@supports not (color-mix(in srgb, currentColor 10%, transparent 90%)) {
  @supports not (color-mix(in oklab, currentColor 10%, transparent 90%)) {
    .se-set5.se-primary:hover {
      /* Ensures a consistent darker hover in legacy browsers */
      filter: brightness(0.88);
    }
  }
}

/* Loading State */
.se-loading-spinner {
  display: flex;
  align-items: center;
}

.se-spinner-svg {
  width: 1.125em;
  height: 1.125em;
  animation: se-spin 1s linear infinite;
}

@keyframes se-spin {
  to { transform: rotate(360deg); }
}

/* Reduced Motion: token handles transition (via --btn-transition: none in _button-tokens.scss).
   This block kills hover transform: values that the token cannot suppress. */
@media (prefers-reduced-motion: reduce) {
  .se-set5.se-primary:hover,
  .se-theme-toggle:hover {
    transform: none !important;
  }
}
</style>
